import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import ButtonRadioGroupFocusable from './ButtonRadioGroupFocusable';

interface ButtonRadioValue {
    name: string;
    label: string;
    description?: string;
    style?: any;
}

interface ButtonRadioType {
    name: string;
    label?: string;
    values: Array<ButtonRadioValue>;
    onChange?: (value: any) => void;
    initialValue?: string;
    resetValue?: string;
    ignoreFocusKey?: boolean;
    isHorizontal?: boolean;
    selectOnArrowKeys?: boolean;
    ref?: React.Ref<any>;
}

const horizontalStyles = {
    display: 'flex',
    gap: 12,
};

const NEXT_KEYS = ['ArrowDown', 'ArrowRight'];
const PREVIOUS_KEYS = ['ArrowUp', 'ArrowLeft'];

export const ButtonRadioGroup: React.FC<ButtonRadioType> = forwardRef(
    ({ name, label, values, onChange, initialValue = null, ignoreFocusKey, isHorizontal, resetValue, selectOnArrowKeys = true }, ref) => {
        const [selectedValue, setSelectedValue] = useState(initialValue);
        const optionRefs = useRef<Array<HTMLLabelElement | null>>([]);

        useImperativeHandle(
            ref,
            () => ({
                forceReset() {
                    if (resetValue) setSelectedValue(resetValue);
                    else console.log('resetValue not set!');
                },
            }),
            []
        );

        const select = (index: number) => {
            const targetValue = values[index].name;

            if (targetValue === selectedValue) return;

            if (onChange) {
                onChange(targetValue);
            }
            setSelectedValue(targetValue);
        };

        const selectedIndex = values.findIndex((value) => value.name === selectedValue);
        const tabStopIndex = selectedIndex === -1 ? 0 : selectedIndex;

        const handleKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>, index: number) => {
            let nextIndex: number;

            if (NEXT_KEYS.includes(event.key)) nextIndex = (index + 1) % values.length;
            else if (PREVIOUS_KEYS.includes(event.key)) nextIndex = (index - 1 + values.length) % values.length;
            else if (event.key === 'Home') nextIndex = 0;
            else if (event.key === 'End') nextIndex = values.length - 1;
            else if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                select(index);
                return;
            } else return;

            event.preventDefault();
            optionRefs.current[nextIndex]?.focus();

            if (selectOnArrowKeys) {
                select(nextIndex);
            }
        };

        return (
            <fieldset data-test-id={'Radio-Button'} role="radiogroup" aria-label={label} style={isHorizontal ? horizontalStyles : {}}>
                {values.map((value, i) => (
                    <ButtonRadioGroupFocusable
                        name={name}
                        value={value}
                        index={i}
                        key={i}
                        ref={(element) => {
                            optionRefs.current[i] = element;
                        }}
                        onChange={() => select(i)}
                        onKeyDown={(event) => handleKeyDown(event, i)}
                        checked={selectedValue === value.name}
                        isTabStop={i === tabStopIndex}
                        ignoreFocusKey={ignoreFocusKey}
                        style={value.style}
                        isHorizontal={isHorizontal}
                    />
                ))}
            </fieldset>
        );
    }
);

ButtonRadioGroup.displayName = 'ButtonRadioGroup';
